import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-14 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">分类未找到</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 同人创作指南`,
    description: category.description,
    url: `https://doujin-guide.com/category/${category.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://doujin-guide.com/article/${article.slug}`,
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(collectionPageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Category Header */}
          <section className="relative w-full py-16 md:py-24 overflow-hidden bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="max-w-3xl">
                <div className="mb-4">
                  <Breadcrumb
                    items={[
                      { label: '首页', href: '/' },
                      { label: category.name },
                    ]}
                  />
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center bg-accent-100">
                    <i className={`${category.icon} text-2xl md:text-3xl text-accent-500`} />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                      {category.name}
                    </h1>
                    <p className="text-sm text-foreground-500 mt-1">{category.count} 篇文章</p>
                  </div>
                </div>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {category.description}
                </p>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-6 mb-8">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-200'
                  }`}
                >
                  <i className={cat.icon} />
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Articles Grid */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14 md:pb-20">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    title={article.title}
                    excerpt={article.excerpt}
                    image={article.image}
                    category={article.category}
                    categorySlug={article.categorySlug}
                    date={article.date}
                    readTime={article.readTime}
                    slug={article.slug}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-14">
                <i className="ri-inbox-line text-4xl text-foreground-400 mb-4 block" />
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}